import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { ReactComponent as TickSelectIcon } from '../../icons/tick-select.svg';
import Lightbox from 'yet-another-react-lightbox';
import FileLink from './FileLink';
import { ResolvedBackground } from './ResolvedImage';
import { useResolvedFileUrls } from './useResolvedFileUrl';
import 'yet-another-react-lightbox/styles.css';
import './image-card.scss';

export interface ImageCardField {
  label: string;
  value: string;
}

export interface ImageCardLink {
  label: string;
  url: string;
}

export interface ImageCardProps {
  id: string;
  title: string;
  blurb?: string;
  imageUrl?: string;
  additionalImages?: string[];
  fields?: ImageCardField[];
  links?: ImageCardLink[];
  selected?: boolean;
  onSelect?: () => void;
  readOnly?: boolean;
  /** Accent colour for links */
  accentColor?: string;
}

const ImageCard: React.FC<ImageCardProps> = ({
  id,
  title,
  blurb,
  imageUrl,
  additionalImages,
  fields,
  links,
  selected = false,
  onSelect,
  readOnly = false,
  accentColor,
}) => {
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [failedSrcs, setFailedSrcs] = useState<string[]>([]);
  const markLoadFailed = useCallback(
    (src: string) =>
      setFailedSrcs((prev) => (prev.includes(src) ? prev : [...prev, src])),
    [],
  );
  const displayImage = activeImage || imageUrl;

  const allImages = useMemo(() => {
    const imgs: string[] = [];
    if (imageUrl) imgs.push(imageUrl);
    if (additionalImages) {
      for (const url of additionalImages) {
        if (url && !imgs.includes(url)) imgs.push(url);
      }
    }
    return imgs;
  }, [imageUrl, additionalImages]);

  const handleImageClick = useCallback((e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    setActiveImage(url);
  }, []);

  const resolvedImages = useResolvedFileUrls(allImages);
  const resolvedFor = (url: string) =>
    resolvedImages[url] ?? { status: 'loading' as const };
  const showableSrc = (url: string): string | null => {
    const r = resolvedImages[url];
    return r && r.status === 'ready' && !failedSrcs.includes(r.src)
      ? r.src
      : null;
  };
  const slides = useMemo(
    () =>
      allImages.flatMap((url) => {
        const r = resolvedImages[url];
        return r && r.status === 'ready' && !failedSrcs.includes(r.src)
          ? [{ src: r.src }]
          : [];
      }),
    [allImages, resolvedImages, failedSrcs],
  );
  const lightboxIndex =
    lightboxUrl !== null
      ? slides.findIndex((s) => s.src === showableSrc(lightboxUrl))
      : -1;
  const hasImages = !!imageUrl;
  const hasAdditional = additionalImages && additionalImages.length > 0;

  return (
    <div
      className={`image-card${selected ? ' selected' : ''}${readOnly ? ' read-only' : ''}`}
      onClick={onSelect}
      role='button'
      tabIndex={0}
    >
      <div className='image-heavy-content'>
        {hasImages && (
          <div className='images-wrapper'>
            <div className={`card-tick${selected ? ' selected' : ''}`}>
              <TickSelectIcon />
            </div>
            <ResolvedBackground
              resolved={resolvedFor(displayImage!)}
              className='main-image'
              onLoadFailed={markLoadFailed}
              onClick={(e) => {
                e.stopPropagation();
                if (showableSrc(displayImage!)) setLightboxUrl(displayImage!);
              }}
            >
              <div className='internal-image-overlay'></div>
            </ResolvedBackground>
            {hasAdditional && (
              <div className='additional-images'>
                <ResolvedBackground
                  resolved={resolvedFor(imageUrl!)}
                  onLoadFailed={markLoadFailed}
                  className={`additional-image${!activeImage || activeImage === imageUrl ? ' active' : ''}`}
                  onClick={(e) => handleImageClick(e, imageUrl!)}
                />
                {additionalImages!.slice(0, 4).map((url, idx) => (
                  <ResolvedBackground
                    key={idx}
                    resolved={resolvedFor(url)}
                    onLoadFailed={markLoadFailed}
                    className={`additional-image${activeImage === url ? ' active' : ''}`}
                    onClick={(e) => handleImageClick(e, url)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        <div className='card-text'>
          <b>{title}</b>
          {blurb && (
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
              {blurb}
            </ReactMarkdown>
          )}
          {fields && fields.length > 0 && (
            <div className='card-fields'>
              {fields.map((field, idx) => (
                <div key={idx} className='card-field'>
                  <span className='field-label'>{field.label}</span>
                  <span className='field-value'>{field.value}</span>
                </div>
              ))}
            </div>
          )}
          {links && links.length > 0 && (
            <div className='card-links'>
              {links.map((link, idx) => (
                <FileLink
                  key={idx}
                  url={link.url}
                  onClick={(e) => e.stopPropagation()}
                  style={accentColor ? { color: accentColor } : undefined}
                >
                  {link.label}
                </FileLink>
              ))}
            </div>
          )}
        </div>
      </div>
      {slides.length > 0 && (
        <Lightbox
          open={lightboxIndex >= 0}
          close={() => setLightboxUrl(null)}
          index={lightboxIndex}
          slides={slides}
        />
      )}
    </div>
  );
};

export default ImageCard;
